import type { Edge } from '@xyflow/react'
import { ObjectId } from 'lib/ObjectId'
import { NOT_STARTED_STAGE, SCHEDULED_STAGE, systemPipeline } from 'app/components/action-center/systemPipelines'
import type { ListCopy } from 'lib/listCopy'
import type { MessageCategory, WorkflowNode } from 'app/types/workflows'

// The launch set. Six templates, named and scoped by Patrick on 2026-08-19, replacing the fourteen
// exploratory ones that came before (win-backs, no-show rescue, treatment follow-up, new-patient
// welcome, text-in welcome, unscheduled new-patient call, the event-anchored hygiene pair). Those
// are in git history and several were verified working; they were cut to make the gallery a launch
// promise rather than a survey of what the engine can do.
//
// `ready` means it activates and runs today, proven on dev. `coming` means the card is honest about
// the one backend thing it waits on. A template is never shipped in a state where it saves, looks
// active, and quietly does nothing, which is the failure mode that has cost us the most this week.

export type WorkflowTemplate = {
  id: string
  title: string
  description: string
  /** The practice outcome this template serves. Groups the gallery, and is deliberately named for
   *  what a front desk is trying to achieve rather than for the trigger it happens to use.
   *
   *  `System` is the exception: it names an audience, not an outcome, because these templates build
   *  the Action Center's own queues rather than anything a practice is trying to achieve. Only a
   *  super is offered them, and only a super can see the workflows they produce. */
  category:
    | `System`
    | `New patient enquiries`
    | `Recall and reactivation`
    | `Appointments`
    | `After the visit`
    | `PMS sync`
  /** Short badge: the funnel story the card tells. */
  badge:
    | `Speed to lead`
    | `PMS-powered`
    | `Win-back`
    | `Show rate`
    | `Case acceptance`
    | `Call conversion`
    | `Action Center`
  status: `ready` | `coming`
  /** For `coming` cards: the honest one-liner on what backend capability it waits on. */
  gatedNote?: string
  /** Builds a fresh canvas doc (new ids each call). Absent when the shape cannot be drawn yet.
   *  `messageCategory` is the TEXTING category (consent gating), not the gallery group above:
   *  marketing sends are refused unless the contact's smsMarketingConsent is true, so every
   *  template declares deliberately which side of that line it lives on. */
  build?: () => { name: string; nodes: WorkflowNode[]; edges: Edge[]; messageCategory: MessageCategory }
}

const at = { x: 280, y: 40 }
const node = (type: WorkflowNode[`type`], data: Record<string, unknown>): WorkflowNode =>
  ({ id: ObjectId(), type, position: at, data }) as WorkflowNode
const link = (source: WorkflowNode, target: WorkflowNode, sourceHandle?: string): Edge => ({
  id: ObjectId(),
  source: source.id,
  target: target.id,
  sourceHandle,
})

function googleReview() {
  // Asks the moment someone becomes a patient, which is the closest thing the record has to "they
  // just had a good visit". lifecycleStage is one of the eight fields that emit a change event even
  // on PMS-sourced saves (verified 08-10), so this fires whether a human or the sync makes the change.
  const trigger = node(`trigger`, {
    label: `Became a patient`,
    kind: `contact_updated`,
    reenrollment: `ignore`,
    source: `any`,
    conditions: [{ type: `field`, field: `lifecycleStage`, operator: `is`, value: `Patient`, scope: `change` }],
    match: `all`,
  })
  // The review link is the practice's own and cannot be guessed, so it ships as a visible blank
  // rather than a plausible-looking URL that would send patients somewhere wrong.
  const ask = node(`sendSms`, {
    label: `Send SMS`,
    toField: `cellPhone`,
    message: `Thanks for choosing us, {{contact.firstName ?? "and welcome"}}! If we did right by you, a quick Google review helps other families find us: [paste your Google review link]`,
  })
  // MARKETING: a review ask is promotional, so only consented contacts receive it.
  return {
    name: `Google review request`,
    nodes: [trigger, ask],
    edges: [link(trigger, ask)],
    messageCategory: `marketing` as const,
  }
}

function formFollowUp() {
  const trigger = node(`trigger`, {
    label: `Form submitted`,
    kind: `form_created`,
    reenrollment: `ignore`,
    // The engine can only text a CONTACT's number (sendSms reads contact[toField]; a contact-less
    // run is skipped as contact_not_found), so requiring the match is what makes `cellPhone` below
    // real. Without it the builder rightly flagged the recipient as out of scope, and the template
    // opened with two errors on its own SMS steps (found in the 08-24 audit pass).
    requireContactMatch: true,
  })
  // FLIPPED 08-24, the day TRK-5579 landed (api #566), per the decision recorded here: both texts
  // target the phone the form submitted — the number the lead just typed, which beats a stale
  // cellPhone. BOTH, not just the first: if the form phone differs from the stored cell phone, a
  // nudge to a different number would split the conversation across two threads.
  const ack = node(`sendSms`, {
    label: `Send SMS`,
    toField: `phone`,
    message: `Hi {{contact.firstName ?? "there"}}, thanks for reaching out! We'll be in touch shortly. Reply here with any questions.`,
  })
  const wait = node(`waitForReply`, { label: `Wait for reply`, within: 1, unit: `days` })
  // Only the no-reply path continues. A reply ends the run, because the second nudge would otherwise
  // talk over a conversation the front desk is already having.
  const nudge = node(`sendSms`, {
    label: `Send SMS`,
    toField: `phone`,
    message: `Just checking in, {{contact.firstName ?? "there"}}. Want us to find you a time that works?`,
  })
  // TRANSACTIONAL: a direct response to the enquiry they just sent.
  return {
    name: `Form follow-up`,
    messageCategory: `transactional` as const,
    nodes: [trigger, ack, wait, nudge],
    edges: [link(trigger, ack), link(ack, wait), link(wait, nudge, `no`)],
  }
}

function formToCall() {
  const trigger = node(`trigger`, {
    label: `Form submitted`,
    kind: `form_created`,
    reenrollment: `ignore`,
    // Same reason as Form follow-up: the call bridges to the CONTACT's number, so the run needs one.
    requireContactMatch: true,
  })
  // The office number is the practice's own, left blank so validation walks them to it.
  const call = node(`initiateCall`, { label: `Initiate call`, ringNumber: `` })
  const result = node(`waitForCallResult`, { label: `Wait for call result` })
  const task = node(`createTask`, {
    label: `Create task`,
    title: `Call back {{contact.firstName ?? "this lead"}} {{contact.lastName ?? ""}}: form submitted, nobody answered`,
    ownerId: `contact-owner`,
    dueType: `relative`,
    dueInDays: 0,
  })
  // TRANSACTIONAL: it places a call and makes a task; no marketing texting involved.
  return {
    name: `Form to instant call-back`,
    messageCategory: `transactional` as const,
    nodes: [trigger, call, result, task],
    // The no_answer branch is the only one wired: an answered call needs no task, and voicemail is
    // left for the practice to decide, since some want a task there too and some do not.
    edges: [link(trigger, call), link(call, result), link(result, task, `no_answer`)],
  }
}

function missedCallTextback() {
  // "Missed" is two different facts and either alone gets it wrong (measured over 300 dev calls):
  // the outcome misses rang-out calls with no recording to classify, and `answered is false` misses
  // the voicemail pickups most worth a reply. So ANY of the two, ONLY IF inbound — that guard stops
  // "sorry we missed your call" going to someone the practice rang.
  const trigger = node(`trigger`, {
    label: `Missed call`,
    kind: `call_finished_processing`,
    reenrollment: `allow_multiple`,
    requireContactMatch: false,
    conditions: [
      { type: `field`, field: `allPatientOutcomes`, operator: `contains`, value: `Missed Call` },
      { type: `field`, field: `answered`, operator: `is`, value: `false` },
      { type: `field`, field: `direction`, operator: `is`, value: `inbound`, scope: `state` },
    ],
    match: `any`,
    stateMatch: `all`,
  })
  const sms = node(`sendSms`, {
    label: `Send SMS`,
    // The caller's own number, not a contact field: most people who hang up on a dental practice are
    // not patients yet, and a contact field is empty for exactly the person worth texting.
    toField: `callerPhone`,
    message: `Sorry we missed your call, {{contact.firstName ?? "there"}}! We're with patients, so text us here and we'll get right back to you.`,
  })
  // TRANSACTIONAL: a direct reply to the call they just placed.
  return {
    name: `Missed call text-back`,
    nodes: [trigger, sms],
    edges: [link(trigger, sms)],
    messageCategory: `transactional` as const,
  }
}

function appointmentConfirmation() {
  // Two asks, each its own run (trackable-api #524). The engine reads the appointment live from Kolla
  // when an ask is due (TRK-6078), so a C or a front-desk confirmation stands the second ask down.
  const trigger = node(`trigger`, {
    label: `Before an appointment`,
    kind: `appointment_date_reached`,
    reenrollment: `ignore`,
    dateField: `appointmentDateTime`,
    dateOffsets: [
      { amount: 3, unit: `d`, direction: `before` },
      { amount: 1, unit: `d`, direction: `before` },
    ],
    conditions: [
      { type: `field`, field: `status`, operator: `is`, value: `Scheduled`, scope: `state` },
      { type: `field`, field: `confirmed`, operator: `is_not`, value: `true`, scope: `state` },
    ],
    match: `all`,
    stateMatch: `all`,
  })
  // Names the patient, not the contact: on a family phone the parent gets the kids' asks.
  const ask = node(`sendSms`, {
    label: `Ask to confirm`,
    toField: `cellPhone`,
    message: `{{appointment.locationName}}: appointment for {{appointment.patientFirstName}} on {{appointment.date}} at {{appointment.time}}. Reply C to confirm.`,
  })
  // 20 hours, so the 3-day run has finished waiting before the 1-day run asks.
  // One option. A cancel or reschedule option can't use CANCEL: the carrier opts the patient out first.
  const confirmed = { id: ObjectId(), label: `Confirmed`, keywords: [`C`, `CONFIRM`] }
  const wait = node(`waitForKeyword`, { label: `Wait for C`, within: 20, unit: `hours`, options: [confirmed] })
  const confirm = node(`updateAppointment`, { label: `Mark confirmed` })
  const thanks = node(`sendSms`, {
    label: `Thank them`,
    toField: `cellPhone`,
    message: `Thanks, {{appointment.patientFirstName}}'s appointment on {{appointment.date}} at {{appointment.time}} is confirmed. See you then!`,
  })
  return {
    name: `Appointment confirmation`,
    nodes: [trigger, ask, wait, confirm, thanks],
    edges: [link(trigger, ask), link(ask, wait), link(wait, confirm, confirmed.id), link(confirm, thanks)],
    // TRANSACTIONAL: operational appointment messages must reach every patient.
    messageCategory: `transactional` as const,
  }
}

function appointmentReminder() {
  // Separate from the confirmation template because one trigger has one condition set for all of
  // its offsets, and this one must reach confirmed patients too. "The morning of" is 3 hours before:
  // offsets count from the appointment TIME in the location's clock, and the engine rejects zero.
  const trigger = node(`trigger`, {
    label: `Before an appointment`,
    kind: `appointment_date_reached`,
    reenrollment: `ignore`,
    dateField: `appointmentDateTime`,
    dateOffsets: [{ amount: 3, unit: `h`, direction: `before` }],
    conditions: [{ type: `field`, field: `status`, operator: `is`, value: `Scheduled`, scope: `state` }],
    match: `all`,
  })
  const sms = node(`sendSms`, {
    label: `Send SMS`,
    toField: `cellPhone`,
    message: `Hi {{contact.firstName ?? "there"}}, a reminder of today's appointment with us. Reply here if you need to change it.`,
  })
  return {
    name: `Appointment reminder`,
    nodes: [trigger, sms],
    edges: [link(trigger, sms)],
    // TRANSACTIONAL: operational appointment messages must reach every patient.
    messageCategory: `transactional` as const,
  }
}

function hygieneRecall() {
  // Anchored on the patient's real hygiene due date, stored since trackable-api #525 and computed
  // from their own recall interval: verified on dev 08-19 as 120 days for perio patients and 181 for
  // everyone else. That split is the reason this counts from the due date rather than from the last
  // visit. A fixed "+6mo from the last cleaning" would chase perio patients two months late, and
  // they are the ones seen most often.
  //
  // "Due today" is +1 hour rather than zero, because the engine rejects a zero offset outright
  // ("Offset amount must be non-zero", probed 08-19). The due date has no time on it, so +1h lands
  // in the small hours of the due date and quiet hours hold the message until morning.
  const trigger = node(`trigger`, {
    label: `Hygiene due`,
    kind: `contact_date_reached`,
    // The engine requires `ignore` on date triggers ("Scheduled date triggers require reenrollment to
    // be ignore", rejected on save 08-20). It makes sense: each offset already fires once per record,
    // so re-enrollment would be a second copy of a schedule that is already deterministic.
    reenrollment: `ignore`,
    dateField: `hygieneDueDate`,
    dateOffsets: [
      { amount: 30, unit: `d`, direction: `before` },
      { amount: 14, unit: `d`, direction: `before` },
      { amount: 1, unit: `h`, direction: `after` },
      { amount: 1, unit: `mo`, direction: `after` },
    ],
    // Read against the live contact when each send is due, so anyone who books in the meantime drops
    // out of the rest of the ladder without any extra branch.
    conditions: [{ type: `field`, field: `appointmentStatus`, operator: `is_not`, value: `Scheduled`, scope: `state` }],
    match: `all`,
  })
  const sms = node(`sendSms`, {
    label: `Send SMS`,
    toField: `cellPhone`,
    message: `Hi {{contact.firstName ?? "there"}}, you're due for a cleaning. Reply here and we'll find you a time.`,
  })
  // TRANSACTIONAL: healthcare recall for the patient's own treatment, not promotion. Judgment
  // call flagged 08-24; flip to `marketing` if compliance reads it the other way.
  return {
    name: `Hygiene recall`,
    nodes: [trigger, sms],
    edges: [link(trigger, sms)],
    messageCategory: `transactional` as const,
  }
}

// ---------------------------------------------------------------------------
// System templates. The Action Center's own enrolment workflows, captured on 2026-08-31 from the
// three hand-built ones running in Braselton Smiles Studio (wId 696a4077…). They are ours, not a
// practice's: each writes a membership on a `system:` pipeline, which is what makes every workflow
// they produce super-only (`isSystemEnrolmentWorkflow`) without any flag being stored.
//
// Captured faithfully except in two places, both marked CORRECTED below and both proven against the
// live workflows' own run history. The live three still carry the defects; fixing them there is a
// separate job from having a template that produces a working one.
// ---------------------------------------------------------------------------

/** The name a card sits under before anyone has worked it, read from the pipeline rather than
 *  retyped: a membership written on a stage no board draws is one nobody can ever see or clear. */
const treatmentFirstStage = systemPipeline(`system:treatment`)?.stages[0]?.name

function treatmentEnrolment(copy: ListCopy) {
  // CORRECTED: the live workflow stores the engine event `contact_created` with this date filter
  // stranded on it, and has never run once. `contact_date_reached` is the trigger the filter was
  // written for. `deserialize.ts` has no case for the date-anchored events, so it degrades them to
  // `contact_created` on open — which is how a workflow that was built right came to be saved wrong.
  const trigger = node(`trigger`, {
    label: `Treatment plan recorded`,
    kind: `contact_date_reached`,
    // The engine requires `ignore` on date triggers: each offset already fires once per record.
    reenrollment: `ignore`,
    dateField: `lastTreatmentPlanDate`,
    // An hour after, not on the stroke of it. The sync writes the plan date and the value rollup in
    // separate saves, and the condition below has nothing to read until the second one lands.
    dateOffsets: [{ amount: 1, unit: `h`, direction: `after` }],
    // Nothing to chase if none of it is unbooked, and nobody the practice has
    // stopped treating. The status rules match the hygiene gate's: a queue that
    // calls inactive, archived or non-patient records is a queue staff learn to
    // distrust. Left off the original template, which is how one inactive patient
    // reached the live Unscheduled treatment board.
    conditions: [
      { type: `field`, field: `unbookedTreatmentValue`, operator: `greater_than`, value: `0`, scope: `state` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Inactive`, scope: `state` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Archived`, scope: `state` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `NonPatient`, scope: `state` },
    ],
    stateMatch: `all`,
  })
  const enrol = node(`addToPipeline`, {
    label: copy.updateList,
    operation: `add`,
    pipelineId: `system:treatment`,
    pipelineName: systemPipeline(`system:treatment`)?.name,
    stage: treatmentFirstStage,
  })
  return {
    name: `Unscheduled treatment enrolment`,
    nodes: [trigger, enrol],
    edges: [link(trigger, enrol)],
    messageCategory: `transactional` as const,
  }
}

function hygieneEnrolment(copy: ListCopy) {
  // CORRECTED the same way as above, and for the same reason: stored as `contact_created`, zero runs.
  const trigger = node(`trigger`, {
    label: `Hygiene due`,
    kind: `contact_date_reached`,
    reenrollment: `ignore`,
    dateField: `hygieneDueDate`,
    // Thirty days BEFORE the recall date, not after it. Enrolling on the due date
    // means every card lands already overdue; the practice wants the window that
    // the "Hygiene Due (30 Days)" list describes, which opens a month ahead.
    dateOffsets: [{ amount: 30, unit: `d`, direction: `before` }],
    // No entry conditions: the gate below carries the whole rule set, and it is
    // the list's rule set exactly (see the comment on `gate`). A `nearestAppointmentDate`
    // check used to live here and was removed — it is not one of the list's rules,
    // and it excluded broken and cancelled appointments, which are the very people
    // a recall queue exists to chase.
  })
  // The queue's own membership test, kept as a branch rather than folded into the trigger so a
  // near-miss shows up in the run history as a contact that reached the gate and stopped, instead
  // of as a workflow that silently never fires.
  //
  // These five rules ARE `hygieneTabFilters` in `app/lib/hygieneTabs.ts`, the definition behind the
  // "Hygiene Overdue" and "Hygiene Due (30 Days)" contact lists, restated in the workflow's own
  // vocabulary. The board this enrols onto has to hold the same people those lists show, so the two
  // must not drift: `workflowTemplates.spec.ts` compares them clause for clause and fails if either
  // side changes alone.
  const gate = node(`branch`, {
    label: `Branch`,
    match: `all`,
    conditions: [
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `NonPatient` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Inactive` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Archived` },
      { type: `field`, field: `appointmentStatus`, operator: `is_not`, value: `Scheduled` },
      // Perio patients are on their own recall interval and are not this queue's to chase.
      { type: `field`, field: `isPerio`, operator: `is_not`, value: `true` },
      // Adults only, and 18 counts: born on or before the day 18 years ago. Same boundary the
      // hygiene queue's own filter settled on in TRK-5616, said in the workflow's vocabulary.
      { type: `field`, field: `birthDate`, operator: `lesser_equal_than`, value: `$now-18y` },
    ],
  })
  const enrol = node(`addToPipeline`, {
    label: copy.updateList,
    operation: `add`,
    pipelineId: `system:hygiene`,
    pipelineName: systemPipeline(`system:hygiene`)?.name,
    stage: NOT_STARTED_STAGE,
  })
  return {
    name: `Hygiene recall enrolment`,
    nodes: [trigger, gate, enrol],
    edges: [link(trigger, gate), link(gate, enrol, `yes`)],
    messageCategory: `transactional` as const,
  }
}

/** The hygiene stages a card can still be sitting on: everything before an outcome takes it off the
 *  board. Derived rather than listed, so a stage added to the queue is covered the day it appears. */
const hygieneWorkingStages = (systemPipeline(`system:hygiene`)?.stages ?? []).filter((stage) => !stage.exit)

function hygieneExitOnBooking(copy: ListCopy) {
  const trigger = node(`trigger`, {
    label: `Appointment booked`,
    kind: `appointment_created`,
    // Every booking, not just a contact's first: someone can be recalled, book, and be recalled
    // again six months later, and the second booking has to move them too.
    reenrollment: `allow_multiple`,
    // An appointment does not always resolve to a contact, and a run without one has no membership
    // to move.
    requireContactMatch: true,
    match: `all`,
  })
  const gate = node(`branch`, {
    label: `Branch`,
    // CORRECTED: the live workflow asks for ALL of these, which is a contact sitting on four stages
    // at once. Every one of its 14 runs stopped here and no card has ever left the board on booking.
    // A card is on exactly one stage, so the question is ANY.
    match: `any`,
    conditions: hygieneWorkingStages.map((stage) => ({
      type: `field` as const,
      field: `pipelines.key`,
      operator: `is` as const,
      value: `system:hygiene::${stage.name}`,
    })),
  })
  const exit = node(`addToPipeline`, {
    // `move`, not `add`: the branch above has already established they are in this pipeline, and
    // the card should read as what it does. Both compile to the same upsert.
    label: copy.updateList,
    operation: `move`,
    pipelineId: `system:hygiene`,
    pipelineName: systemPipeline(`system:hygiene`)?.name,
    stage: SCHEDULED_STAGE,
  })
  return {
    name: `Hygiene recall exit on booking`,
    nodes: [trigger, gate, exit],
    edges: [link(trigger, gate), link(gate, exit, `yes`)],
    messageCategory: `transactional` as const,
  }
}

function hygieneReentryOnBreak(copy: ListCopy) {
  // The counterpart to the exit above. Without it a card moved to Scheduled on
  // booking stays there when the patient breaks the appointment, so the one
  // group most worth calling is the one group the board stops showing.
  const trigger = node(`trigger`, {
    label: `Appointment broken`,
    kind: `appointment_updated`,
    // Every break, not just the first: a patient can break, be recalled, book,
    // and break again, and each one has to put them back.
    reenrollment: `allow_multiple`,
    requireContactMatch: true,
    // The appointment's own status landing on either value. ANY, because the two
    // words mean the same thing to a recall queue. `status` and not `pmsStatus`:
    // both appear in the change payload and agree wherever both are present, but
    // `status` is on more of them (183 of 300 sampled against 114).
    conditions: [
      { type: `field`, field: `status`, operator: `is`, value: `Broken`, scope: `change` },
      { type: `field`, field: `status`, operator: `is`, value: `Cancelled`, scope: `change` },
    ],
    match: `any`,
  })
  // A break and an immediate rebook arrive as two syncs, up to an hour apart. Read
  // the record straight away and the rebooking has not landed yet, so the patient
  // goes back on the board and comes off again minutes later. A day's wait costs a
  // recall queue nothing and removes the churn.
  const settle = node(`wait`, { label: `Wait`, duration: 1, unit: `days`, businessHoursOnly: false })
  // Did they rebook? ANY: no next appointment, or the nearest one has been and gone.
  //
  // The status rows are the carve-out `hygieneWindow` makes — a broken or cancelled appointment is
  // not a chair, so a date attached to one must not read as a rebooking. They are not redundant
  // with the trigger, which fires on the APPOINTMENT's status change; this reads the CONTACT's
  // rollup a day later, where someone who rebooked shows Scheduled with a future date.
  const rebooked = node(`branch`, {
    label: `Branch`,
    match: `any`,
    conditions: [
      { type: `field`, field: `appointmentStatus`, operator: `is`, value: `Broken` },
      { type: `field`, field: `appointmentStatus`, operator: `is`, value: `Cancelled` },
      { type: `field`, field: `nearestAppointmentDate`, operator: `is_empty` },
      { type: `field`, field: `nearestAppointmentDate`, operator: `lesser_than`, value: `$now` },
    ],
  })
  // And are they someone the board would draw at all? Same five rules as the
  // enrolment gate, plus the recall window, so a break six months before a
  // patient is next due does not put them on the board today.
  const eligible = node(`branch`, {
    label: `Branch`,
    match: `all`,
    conditions: [
      { type: `field`, field: `hygieneDueDate`, operator: `lesser_equal_than`, value: `$now+30d` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `NonPatient` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Inactive` },
      { type: `field`, field: `patientStatus`, operator: `is_not`, value: `Archived` },
      { type: `field`, field: `isPerio`, operator: `is_not`, value: `true` },
      { type: `field`, field: `birthDate`, operator: `lesser_equal_than`, value: `$now-18y` },
    ],
  })
  // Back to the top of the board. The break restarts the chase, so any outreach
  // recorded against the previous attempt is deliberately not carried over.
  const reenrol = node(`addToPipeline`, {
    label: copy.updateList,
    operation: `add`,
    pipelineId: `system:hygiene`,
    pipelineName: systemPipeline(`system:hygiene`)?.name,
    stage: NOT_STARTED_STAGE,
  })
  return {
    name: `Hygiene recall re-entry on break`,
    nodes: [trigger, settle, rebooked, eligible, reenrol],
    edges: [
      link(trigger, settle),
      link(settle, rebooked),
      link(rebooked, eligible, `yes`),
      link(eligible, reenrol, `yes`),
    ],
    messageCategory: `transactional` as const,
  }
}

function commLogFollowUpSync() {
  const trigger = node(`trigger`, {
    label: `PMS comm log added`,
    kind: `comm_log_created`,
    reenrollment: `allow_multiple`,
    conditions: [],
    match: `all`,
  })
  const followUp = node(`logFollowUp`, {
    label: `Log a follow-up`,
    connected: `fromDirection`,
    dateSource: `event`,
    note: `{{activity.commLogChannelLabel}}: {{activity.commLogSubject}} {{activity.commLogNote}}`,
  })
  // Sends nothing to the patient, so the texting category only records which side it sits on.
  return {
    name: `Keep follow-ups in sync with the PMS`,
    nodes: [trigger, followUp],
    edges: [link(trigger, followUp)],
    messageCategory: `transactional` as const,
  }
}

// Personalisation is live: TRK-5498 shipped 08-14 and a real SMS arrived reading "hi Patrick", so
// every template above sends its name as written. Tokens must stay namespaced: the engine resolves
// an unknown token to an empty string with no error, so a bare `{{firstName}}` texts "Hi ,".
// serialize.ts blocks the bare form for exactly that reason.
export const workflowTemplates = (copy: ListCopy): WorkflowTemplate[] => [
  {
    id: `system_treatment_enrolment`,
    title: `Unscheduled treatment enrolment`,
    description: `Puts a patient with treatment still unbooked onto the Unscheduled treatment board, an hour after their plan is recorded.`,
    category: `System`,
    badge: `Action Center`,
    status: `ready`,
    build: () => treatmentEnrolment(copy),
  },
  {
    id: `system_hygiene_enrolment`,
    title: `Hygiene recall enrolment`,
    description: `Puts an adult patient onto the Hygiene Recall board 30 days before their recall is due.`,
    category: `System`,
    badge: `Action Center`,
    status: `ready`,
    build: () => hygieneEnrolment(copy),
  },
  {
    id: `system_hygiene_exit`,
    title: `Hygiene recall exit on booking`,
    description: `Moves a patient to Scheduled on the Hygiene Recall board the moment they book, which takes their card off it.`,
    category: `System`,
    badge: `Action Center`,
    status: `ready`,
    build: () => hygieneExitOnBooking(copy),
  },
  {
    id: `system_hygiene_reentry`,
    title: `Hygiene recall re-entry on break`,
    description: `Puts a patient back on the Hygiene Recall board when they break or cancel and do not rebook.`,
    category: `System`,
    badge: `Action Center`,
    status: `ready`,
    build: () => hygieneReentryOnBreak(copy),
  },
  {
    id: `form_follow_up`,
    title: `Form follow-up`,
    description: `Text every new form lead instantly, then nudge once more if they go quiet. A reply ends it.`,
    category: `New patient enquiries`,
    badge: `Speed to lead`,
    status: `ready`,
    build: formFollowUp,
  },
  {
    id: `form_to_call`,
    title: `Form to instant call-back`,
    description: `Ring the office the moment a form lands, and task the front desk if nobody picks up.`,
    category: `New patient enquiries`,
    badge: `Speed to lead`,
    status: `ready`,
    build: formToCall,
  },
  {
    id: `missed_call_textback`,
    title: `Missed call text-back`,
    description: `An inbound call nobody picked up gets one text, sent to the number that called.`,
    category: `New patient enquiries`,
    badge: `Speed to lead`,
    status: `ready`,
    build: missedCallTextback,
  },
  {
    id: `appointment_confirmation`,
    title: `Appointment confirmation`,
    description: `Asks 3 days and 1 day before. Replying C confirms it in your practice system; any other reply waits in your inbox.`,
    category: `Appointments`,
    badge: `Show rate`,
    status: `ready`,
    build: appointmentConfirmation,
  },
  {
    id: `appointment_reminder`,
    title: `Appointment reminder`,
    description: `One text 3 hours before. A cancelled, broken or completed appointment gets nothing.`,
    category: `Appointments`,
    badge: `Show rate`,
    status: `ready`,
    build: appointmentReminder,
  },
  {
    id: `hygiene_recall`,
    title: `Hygiene recall`,
    description: `Four nudges around the hygiene due date: a month before, two weeks before, on the day, and a month overdue.`,
    category: `Recall and reactivation`,
    badge: `PMS-powered`,
    status: `ready`,
    build: hygieneRecall,
  },
  {
    id: `google_review`,
    title: `Google review request`,
    description: `Ask for a Google review the moment someone becomes a patient.`,
    category: `After the visit`,
    badge: `PMS-powered`,
    status: `ready`,
    build: googleReview,
  },
  {
    id: `comm_log_follow_up_sync`,
    title: `Keep follow-ups in sync with the PMS`,
    description: `Log a follow-up in Trackable whenever another tool writes to the PMS comm log.`,
    category: `PMS sync`,
    badge: `PMS-powered`,
    status: `coming`,
    gatedNote: `Waits on the API to emit comm log events and create follow-ups (TRK-6138).`,
    build: commLogFollowUpSync,
  },
]

export function workflowTemplateById(id: string | null | undefined, copy: ListCopy): WorkflowTemplate | undefined {
  return id ? workflowTemplates(copy).find((template) => template.id === id) : undefined
}
